import { useEffect, useState } from 'react';
import { FileText, Plus } from 'lucide-react';
import { operationsApi, type DocumentType } from '../api';
import { PageHeader } from '@/lib/ui';

export function DocumentTypeManagerPage() {
  const [items, setItems] = useState<DocumentType[]>([]);
  const [showCreate, setShowCreate] = useState(false);

  const refresh = async () => setItems(await operationsApi.listDocumentTypes());

  useEffect(() => {
    void refresh();
  }, []);

  return (
    <div className="p-6">
      <PageHeader
        title="Document Types"
        icon={FileText}
        subtitle="Define document slugs (Registration, License, Insurance…) per scope. The expiry-alert ladder fires at each lead-day rung."
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            <Plus className="h-4 w-4" />
            New type
          </button>
        }
      />

      <table className="mt-6 w-full text-sm">
        <thead className="text-left text-xs uppercase text-muted-foreground">
          <tr className="border-b border-border">
            <th className="py-2 pr-4">Slug</th>
            <th className="py-2 pr-4">Name</th>
            <th className="py-2 pr-4">Scope</th>
            <th className="py-2 pr-4">Required</th>
            <th className="py-2 pr-4">Lead days</th>
            <th className="py-2"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((t) => (
            <tr key={t.id} className="border-b border-border/60">
              <td className="py-2 pr-4 font-mono text-xs">{t.slug}</td>
              <td className="py-2 pr-4">{t.name}</td>
              <td className="py-2 pr-4">{t.scope}</td>
              <td className="py-2 pr-4">{t.is_required ? '✓' : '—'}</td>
              <td className="py-2 pr-4 font-mono text-xs">{t.expiry_lead_days.join(' / ')}</td>
              <td className="py-2 text-right text-xs">
                <button
                  className="text-primary hover:underline"
                  onClick={async () => {
                    await operationsApi.patchDocumentType(t.id, {
                      is_enabled: !t.is_enabled,
                    });
                    await refresh();
                  }}
                >
                  {t.is_enabled ? 'Disable' : 'Enable'}
                </button>
              </td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr>
              <td colSpan={6} className="py-6 text-center text-sm text-muted-foreground">
                No document types configured.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {showCreate && (
        <DocumentTypeCreateModal
          onClose={() => setShowCreate(false)}
          onCreated={async () => {
            setShowCreate(false);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

function DocumentTypeCreateModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [scope, setScope] = useState<'ASSET' | 'FIELD_USER'>('ASSET');
  const [required, setRequired] = useState(false);
  const [leadDays, setLeadDays] = useState('30, 14, 7, 1');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const days = leadDays
        .split(',')
        .map((s) => Number(s.trim()))
        .filter((n) => Number.isFinite(n) && n > 0);
      await operationsApi.createDocumentType({
        slug: slug.trim(),
        name: name.trim(),
        scope,
        is_required: required,
        is_enabled: true,
        expiry_lead_days: days,
      });
      onCreated();
    } catch (e) {
      setError((e as Error).message || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4">
      <form onSubmit={onSubmit} className="card w-full max-w-md space-y-3 bg-surface p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">New document type</h2>
          <button type="button" onClick={onClose}>
            ✕
          </button>
        </div>
        {error && (
          <div className="rounded bg-rose-500/15 px-3 py-2 text-sm text-rose-300">{error}</div>
        )}
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-xs uppercase text-muted-foreground">Slug *</span>
          <input
            required
            pattern="[a-z0-9_-]+"
            className="input font-mono"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-xs uppercase text-muted-foreground">Name *</span>
          <input
            required
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-xs uppercase text-muted-foreground">Scope *</span>
          <select
            className="input"
            value={scope}
            onChange={(e) => setScope(e.target.value as 'ASSET' | 'FIELD_USER')}
          >
            <option value="ASSET">ASSET</option>
            <option value="FIELD_USER">FIELD_USER</option>
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={required}
            onChange={(e) => setRequired(e.target.checked)}
          />
          Required
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-xs uppercase text-muted-foreground">
            Lead days (comma separated)
          </span>
          <input
            className="input font-mono"
            value={leadDays}
            onChange={(e) => setLeadDays(e.target.value)}
          />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" disabled={saving} className="btn-primary">
            {saving ? 'Saving…' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
}
